(() => {
  const root = document.querySelector('[data-spd-section]');
  if (!root) return;

  const moneyFormat =
    (window.Shopify && window.Shopify.money_format) ||
    root.dataset.moneyFormat ||
    '${{amount}}';

  const formatMoney = (cents) => {
    const value = Number(cents || 0);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(value, moneyFormat);
    }
    return `$${(value / 100).toFixed(2)}`;
  };

  // ==================== Helper Utilities ====================
  const toggleVisibility = (selector, type) => {
    root.querySelectorAll(selector).forEach((el) => {
      const matchesType = el.dataset.purchase === type;
      el.style.display = matchesType ? 'flex' : 'none';
    });
  };

  // ==================== State Management ====================
  const state = {
    purchase: 'planned',
    frequency: '4w',
  };

  const isMember = root.dataset.isMember === 'true';

  const cartState = {
    items: new Map(),
    initialOtherItemsCount: 0,
  };

  // Quantities tapped in but not yet confirmed by /cart/add.js, so a cart sync for an
  // earlier add doesn't briefly wipe a later item from the footer.
  const pendingAdds = new Map();
  const releasePendingAdd = (variantId, quantity) => {
    const remaining = (pendingAdds.get(variantId) || 0) - quantity;
    if (remaining > 0) pendingAdds.set(variantId, remaining);
    else pendingAdds.delete(variantId);
  };

  let lastCartStamp = null;
  let syncInFlight = false;
  let syncQueued = false;
  const qtyUpdateTimers = new Map();
  const pendingQtyUpdates = new Map();
  let frequencyUpdateTimer = null;

  // ==================== Skio Dynamic Box ====================
  // Box UUID is hardcoded in data-box-id on the section root (set in Liquid)
  const skioBoxId = root.dataset.boxId || '';

  // ==================== DOM Elements ====================
  const titleEl = root.querySelector('[data-spd-title]');
  const subtitleEl = root.querySelector('[data-spd-subtitle]');
  const benefitBlocks = root.querySelectorAll('[data-benefits]');
  const purchaseButtons = root.querySelectorAll('[data-purchase]');
  const frequencyInputs = root.querySelectorAll('input[name="spd-frequency"]');
  const frequencySection = root.querySelector('[data-spd-frequency]');
  const checkoutBtn = root.querySelector('.spd-checkout-btn');
  const checkoutMain = root.querySelector('[data-spd-checkout-main]');
  const checkoutSub = root.querySelector('[data-spd-checkout-sub]');
  const toastArea = root.querySelector('[data-spd-toast-area]');

  let frequencyLoadingTimer;
  let toastTimer = null;

  const isTrue = (value) => value === 'true';
  const parseInteger = (value, fallback = 0) => {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const showToast = (message) => {
    if (!toastArea || !message) return;
    toastArea.innerHTML = '';

    const toast = document.createElement('div');
    toast.className = 'spd-toast';
    toast.textContent = message;
    toastArea.appendChild(toast);

    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => {
        if (toast.parentNode === toastArea) {
          toast.remove();
        }
      }, 220);
    }, 2600);
  };

  const getInventoryState = (product) => {
    const inventoryManagement = product?.dataset.inventoryManagement || '';
    const inventoryPolicy = product?.dataset.inventoryPolicy || '';
    const inventoryQuantity = parseInteger(product?.dataset.inventoryQuantity, 0);
    const isTracked = inventoryManagement === 'shopify' && inventoryPolicy === 'deny';
    const maxAvailable = isTracked ? Math.max(inventoryQuantity, 0) : Infinity;
    const isOutOfStock = isTracked && maxAvailable <= 0;

    return {
      isTracked,
      maxAvailable,
      isOutOfStock,
      hasLimit: Number.isFinite(maxAvailable),
    };
  };

  const getMaxAvailableMessage = (maxAvailable) => {
    if (maxAvailable <= 0) return 'This item is out of stock right now.';
    return `Only ${maxAvailable} ${maxAvailable === 1 ? 'item is' : 'items are'} available right now.`;
  };

  const clampQuantityToInventory = (product, nextQuantity, { showLimitToast = false } = {}) => {
    const inventory = getInventoryState(product);
    const safeNextQuantity = Math.max(0, parseInteger(nextQuantity, 0));

    if (!inventory.isTracked) {
      return { quantity: safeNextQuantity, inventory };
    }

    if (inventory.isOutOfStock) {
      if (showLimitToast) showToast(getMaxAvailableMessage(0));
      return { quantity: 0, inventory };
    }

    if (safeNextQuantity > inventory.maxAvailable) {
      if (showLimitToast) showToast(getMaxAvailableMessage(inventory.maxAvailable));
      return { quantity: inventory.maxAvailable, inventory };
    }

    return { quantity: safeNextQuantity, inventory };
  };

  const updateStockNotice = (product, inventory = getInventoryState(product)) => {
    const note = product?.querySelector('[data-spd-stock-note]');
    if (!note) return;

    note.textContent = '';
    note.classList.add('is-hidden');
  };

  const syncProductAvailabilityUI = (product) => {
    if (!product) return;
    const inventory = getInventoryState(product);
    const input = product.querySelector('.spd-qty__input');
    const minusBtn = product.querySelector('[data-qty="minus"]');
    const plusBtn = product.querySelector('[data-qty="plus"]');
    const addBtn = product.querySelector('.spd-add-btn');
    const quantity = Math.max(0, parseInteger(input?.value, 0));
    const atLimit = inventory.hasLimit && quantity >= inventory.maxAvailable;

    if (input) {
      input.value = quantity;
      if (inventory.hasLimit) input.setAttribute('max', String(inventory.maxAvailable));
      else input.removeAttribute('max');
      input.disabled = inventory.isOutOfStock;
      input.setAttribute('aria-disabled', inventory.isOutOfStock ? 'true' : 'false');
    }

    if (minusBtn) {
      minusBtn.disabled = inventory.isOutOfStock && quantity <= 0;
    }

    if (plusBtn) {
      plusBtn.disabled = !inventory.isOutOfStock && atLimit;
      plusBtn.setAttribute('aria-disabled', plusBtn.disabled ? 'true' : 'false');
    }

    if (addBtn) {
      if (inventory.isOutOfStock) {
        addBtn.classList.add('is-disabled');
        addBtn.setAttribute('aria-disabled', 'true');
        addBtn.textContent = 'Out of Stock';
      } else {
        addBtn.classList.remove('is-disabled');
        addBtn.removeAttribute('aria-disabled');
        if (!addBtn.classList.contains('is-loading')) {
          addBtn.disabled = false;
        }
      }
    }

    product.classList.toggle('is-out-of-stock', inventory.isOutOfStock);
    updateStockNotice(product, inventory);
  };

  const setProductQuantityValue = (product, nextQuantity, options = {}) => {
    if (!product) return 0;
    const input = product.querySelector('.spd-qty__input');
    const { quantity } = clampQuantityToInventory(product, nextQuantity, options);
    if (input) input.value = quantity;
    syncProductAvailabilityUI(product);
    return quantity;
  };

  const getCartStamp = (cartData) => {
    if (!cartData) return null;
    if (cartData.updated_at) return cartData.updated_at;
    if (Array.isArray(cartData.items)) {
      const itemStamp = cartData.items
        .map((item) => `${item.variant_id}:${item.quantity}`)
        .join('|');
      return `${cartData.item_count || 0}:${itemStamp}`;
    }
    return null;
  };

  const isDuplicateCartData = (cartData) => {
    const stamp = getCartStamp(cartData);
    return Boolean(stamp && stamp === lastCartStamp);
  };

  const setLastCartStamp = (cartData) => {
    const stamp = getCartStamp(cartData);
    if (stamp) lastCartStamp = stamp;
  };

  // Plan attributes look like data-plan-id-2w-effective. A dash followed by a digit is
  // NOT camel-cased by the dataset API (it becomes dataset['planId-2wEffective']), so
  // dataset.planId2wEffective was always undefined and every add fell back to a slow
  // /products/<handle>?view=json-meta fetch. Read the attributes directly instead.
  const getPlanAttr = (product, name) => (product && product.getAttribute(`data-${name}`)) || '';

  const getPlanIdFromDataset = (product, frequency) => {
    if (frequency !== '2w' && frequency !== '4w') return '';
    const effective = getPlanAttr(product, `plan-id-${frequency}-effective`);
    if (effective) return effective;
    const memberActive = isTrue(getPlanAttr(product, `plan-${frequency}-member-active`));
    const stdActive = isTrue(getPlanAttr(product, `plan-${frequency}-std-active`));
    if (isMember && memberActive) return getPlanAttr(product, `plan-id-${frequency}-member`);
    if (stdActive) return getPlanAttr(product, `plan-id-${frequency}-std`);
    return '';
  };

  const getPlanIdFromMeta = (meta, frequency) => {
    if (!meta || !meta.subscription_plans) return '';
    const plans = meta.subscription_plans;
    const isMemberMeta = plans.is_member;

    if (frequency === '2w') {
      if (isMemberMeta && plans.every_2_weeks?.member?.active) {
        return plans.every_2_weeks.member.id;
      }
      if (plans.every_2_weeks?.standard?.active) {
        return plans.every_2_weeks.standard.id;
      }
      return '';
    }

    if (frequency === '4w') {
      if (isMemberMeta && plans.every_4_weeks?.member?.active) {
        return plans.every_4_weeks.member.id;
      }
      if (plans.every_4_weeks?.standard?.active) {
        return plans.every_4_weeks.standard.id;
      }
      return '';
    }

    return '';
  };

  const fetchProductMeta = async (handle) => {
    if (!handle) return null;
    if (window.productMetaCache && window.productMetaCache[handle]) {
      return window.productMetaCache[handle];
    }

    if (window.Shopify && typeof window.Shopify.fetchProductsMeta === 'function') {
      const products = await window.Shopify.fetchProductsMeta([handle], 1);
      return products && products.length ? products[0] : null;
    }

    try {
      const res = await fetch(`/products/${handle}?view=json-meta`, { credentials: 'same-origin' });
      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const updateTolstoyArrowVisibility = (container) => {
    if (!container || !(container instanceof HTMLElement)) return;
    if (container.clientWidth === 0) return;

    const wrapper = container.closest('tolstoy-stories') || container.closest('._storiesVideoContainer_1v61q_12');
    if (!wrapper) return;

    const prevBtn = wrapper.querySelector('.tolstoy-stories-previous-button');
    const nextBtn = wrapper.querySelector('.tolstoy-stories-next-button');
    const isScrollable = container.scrollWidth > (container.clientWidth + 10);

    if (!isScrollable) {
      if (prevBtn) prevBtn.setAttribute('data-visibility', 'hidden');
      if (nextBtn) nextBtn.setAttribute('data-visibility', 'hidden');
      return;
    }

    const isAtStart = container.scrollLeft <= 10;
    const isAtEnd = (container.scrollLeft + container.clientWidth) >= (container.scrollWidth - 10);

    if (prevBtn) {
      if (isAtStart) prevBtn.setAttribute('data-visibility', 'hidden');
      else prevBtn.removeAttribute('data-visibility');
    }

    if (nextBtn) {
      if (isAtEnd) nextBtn.setAttribute('data-visibility', 'hidden');
      else nextBtn.removeAttribute('data-visibility');
    }
  };

  const tolstoyResizeObserver = new ResizeObserver((entries) => {
    entries.forEach((entry) => updateTolstoyArrowVisibility(entry.target));
  });

  const attachTolstoyListeners = (scope) => {
    if (!scope) return;
    scope.querySelectorAll('.tolstoy-stories-tiles-container, ._tilesContainer_1v61q_19').forEach((container) => {
      tolstoyResizeObserver.observe(container);
      if (container.dataset.spdTolstoyBound !== 'true') {
        container.addEventListener('scroll', () => updateTolstoyArrowVisibility(container));
        container.dataset.spdTolstoyBound = 'true';
      }
      updateTolstoyArrowVisibility(container);
    });
  };

  const watchTolstoyLoaded = (panel) => {
    if (!panel) return;

    const runWhenReady = () => {
      const loadedNodes = panel.querySelectorAll('tolstoy-stories[data-status="loaded"]');
      if (!loadedNodes.length) return false;
      attachTolstoyListeners(panel);
      requestAnimationFrame(() => attachTolstoyListeners(panel));
      setTimeout(() => attachTolstoyListeners(panel), 200);
      return true;
    };

    if (runWhenReady()) return;

    let attempts = 0;
    const maxAttempts = 30;
    const interval = setInterval(() => {
      attempts += 1;
      if (runWhenReady() || attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 120);

    const observer = new MutationObserver(() => {
      if (runWhenReady()) {
        observer.disconnect();
      }
    });
    observer.observe(panel, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-status', 'style', 'data-visibility'],
    });

    setTimeout(() => {
      observer.disconnect();
      clearInterval(interval);
    }, 5000);
  };

  const refreshTolstoyInPanel = (panel) => {
    if (!panel) return;

    const stories = panel.querySelectorAll('tolstoy-stories');
    if (!stories.length) return;

    stories.forEach((node) => {
      // Re-mount to ensure the app binds correctly inside dynamic/hidden content.
      const cloned = node.cloneNode(true);
      node.replaceWith(cloned);
    });

    // Force layout reflow after panel is visible so story rail can recalculate widths.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
        panel.dispatchEvent(new CustomEvent('spd:tolstoy-refresh', { bubbles: true }));
        attachTolstoyListeners(panel);
        watchTolstoyLoaded(panel);
      });
    });

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      panel.dispatchEvent(new CustomEvent('spd:tolstoy-refresh', { bubbles: true }));
      attachTolstoyListeners(panel);
      watchTolstoyLoaded(panel);
    }, 180);

    setTimeout(() => {
      attachTolstoyListeners(panel);
      watchTolstoyLoaded(panel);
    }, 450);
  };

  // "Learn More" panels are rendered empty and fetched on first open
  // (templates/product.spd-learn.liquid) to keep the page light on phones.
  const learnPanelRequests = new WeakMap();
  const loadLearnPanel = (panel) => {
    const url = panel.dataset.learnUrl;
    if (!url || panel.dataset.learnLoaded === 'true') return Promise.resolve();
    if (learnPanelRequests.has(panel)) return learnPanelRequests.get(panel);

    const request = fetch(url, { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) throw new Error(`Learn panel ${res.status}`);
        return res.text();
      })
      .then((html) => {
        panel.innerHTML = html;
        panel.dataset.learnLoaded = 'true';
      })
      .catch((err) => {
        console.error(err);
        const productUrl = url.split('?')[0];
        panel.innerHTML = `<p class="spd-learn-copy">Details couldn't load. <a href="${productUrl}">View the product page</a>.</p>`;
      })
      .finally(() => learnPanelRequests.delete(panel));

    learnPanelRequests.set(panel, request);
    return request;
  };

  const updateHeader = () => {
    if (titleEl) {
      titleEl.textContent =
        state.purchase === 'one-time'
          ? root.dataset.titleOnetime
          : root.dataset.titlePlanned;
    }
    if (subtitleEl) {
      subtitleEl.textContent =
        state.purchase === 'one-time'
          ? root.dataset.subtitleOnetime
          : root.dataset.subtitlePlanned;
    }
  };

  const setFrequency = (value, { syncPurchase = true, triggerCartUpdate = true } = {}) => {
    state.frequency = value;
    frequencyInputs.forEach((input) => {
      input.checked = input.value === value;
    });
    if (syncPurchase && value) {
      setPurchaseType('planned', { syncFrequency: false, triggerCartUpdate: false });
    }
    updateLocalTotal();
    flashFrequencyLoading();
    if (triggerCartUpdate) {
      scheduleFrequencyCartUpdate();
    }
  };

  const setPurchaseType = (type, { syncFrequency = true, triggerCartUpdate = true } = {}) => {
    state.purchase = type;
    const isPlanned = type === 'planned';

    root.classList.toggle('is-planned-mode', isPlanned);
    root.classList.toggle('is-onetime-mode', !isPlanned);

    purchaseButtons.forEach((button) => {
      const isActive = button.dataset.purchase === type;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    benefitBlocks.forEach((block) => {
      const isMatch = block.dataset.benefits === type;
      block.classList.toggle('is-hidden', !isMatch);
    });

    if (frequencySection) {
      frequencySection.classList.toggle('is-hidden', !isPlanned);
    }

    toggleVisibility('.spd-hero-footer', type);

    frequencyInputs.forEach((input) => {
      input.disabled = !isPlanned;
    });

    if (syncFrequency && isPlanned) {
      setFrequency(state.frequency || '4w', { syncPurchase: false, triggerCartUpdate: false });
    }

    updateHeader();
    updateNextDeliveryDate();
    updateLocalTotal();
    if (triggerCartUpdate) {
      scheduleFrequencyCartUpdate();
    }
  };

  const toggleGlobalLoading = (isLoading) => {
    root.classList.toggle('is-loading', isLoading);
    if (checkoutBtn) checkoutBtn.classList.toggle('is-loading', isLoading);

    const controlSelectors = [
      '.spd-toggle__btn',
      'input[name="spd-frequency"]',
      '.spd-qty__btn',
      '.spd-qty__input',
      '.spd-add-btn',
      '.spd-cat-button',
      '.spd-checkout-btn'
    ];

    root.querySelectorAll(controlSelectors.join(',')).forEach((el) => {
      if (isLoading) {
        el.setAttribute('disabled', 'true');
        el.setAttribute('aria-disabled', 'true');
      } else {
        el.removeAttribute('disabled');
        el.removeAttribute('aria-disabled');
      }
    });
  };

  const flashFrequencyLoading = () => {
    if (!frequencySection) return;
    frequencySection.classList.add('is-loading');
    if (checkoutBtn) checkoutBtn.classList.add('is-loading');
    clearTimeout(frequencyLoadingTimer);
    frequencyLoadingTimer = setTimeout(() => {
      frequencySection.classList.remove('is-loading');
      if (checkoutBtn) checkoutBtn.classList.remove('is-loading');
    }, 300);
  };

  const getProductPricingModel = (product) => {
    const onetime = parseInt(product.dataset.onetimePrice || '0', 10);
    const compareAt = parseInt(product.dataset.compareAtPrice || '0', 10);
    // data-price-2w / data-price-4w have the same dash-digit dataset issue as the plan IDs.
    const planned2w = parseInt(getPlanAttr(product, 'price-2w') || onetime, 10);
    const planned4w = parseInt(getPlanAttr(product, 'price-4w') || onetime, 10);
    const hasPlannedDisplay = isTrue(product.dataset.hasPlannedDisplay);
    const plannedDisplayPrice = hasPlannedDisplay
      ? parseInt(product.dataset.plannedDisplayPrice || '0', 10)
      : 0;

    // Match the planned price the card shows (tier_2_price when set), which is what
    // the cart ends up charging.
    const activePlannedPrice = hasPlannedDisplay && plannedDisplayPrice > 0
      ? plannedDisplayPrice
      : (state.frequency === '2w' ? planned2w : planned4w);
    const activePrice = state.purchase === 'one-time' ? onetime : activePlannedPrice;

    return {
      onetime,
      compareAt,
      hasPlannedDisplay,
      plannedDisplayPrice,
      activePlannedPrice,
      activePrice,
    };
  };

  const getSectionVariantIds = () => {
    return new Set(
      Array.from(root.querySelectorAll('.spd-product'))
        .map((product) => parseInt(product.dataset.variantId || '0', 10))
        .filter((id) => Number.isFinite(id) && id > 0)
    );
  };

  const updateProductUI = (product, quantity) => {
    const safeQuantity = setProductQuantityValue(product, quantity);

    const button = product.querySelector('.spd-add-btn');
    if (!button) return;

    const inventory = getInventoryState(product);

    if (inventory.isOutOfStock) {
      button.classList.remove('is-added');
      button.textContent = 'Out of Stock';
      syncProductAvailabilityUI(product);
      return;
    }

    if (safeQuantity > 0) {
      button.classList.add('is-added');
      button.textContent = 'Added to Order';
    } else {
      button.classList.remove('is-added');
      button.textContent = button.dataset.label || 'Add to Order';
    }

    syncProductAvailabilityUI(product);
  };

  const syncProductUIs = () => {
    root.querySelectorAll('.spd-product').forEach((product) => {
      const variantId = parseInt(product.dataset.variantId || '0', 10);
      const quantity = cartState.items.get(variantId) || 0;
      updateProductUI(product, quantity);
    });
    renderCartList();
  };

  const renderCartList = () => {
    const listEl = root.querySelector('[data-spd-cart-list]');
    const emptyEl = root.querySelector('[data-spd-cart-empty]');
    if (!listEl) return;

    listEl.innerHTML = '';
    let hasItems = false;
    const seen = new Set();

    root.querySelectorAll('.spd-product').forEach((product) => {
      const variantId = parseInt(product.dataset.variantId || '0', 10);
      if (seen.has(variantId)) return;
      seen.add(variantId);
      const qty = cartState.items.get(variantId) || 0;
      if (qty <= 0) return;
      hasItems = true;

      const titleEl = product.querySelector('.spd-product__title [data-product-title]');
      const imgEl = product.querySelector('.spd-product__image img');
      const title = titleEl ? titleEl.textContent.trim() : '';
      const imgSrc = imgEl ? imgEl.src : '';

      // Read price from product card DOM — matches exactly what the card shows
      const pricing = getProductPricingModel(product);
      const plannedRow = product.querySelector('[data-planned-row]');
      const plannedPriceEl = product.querySelector('[data-planned-price]');
      const onetimePriceEl = product.querySelector('[data-onetime-price]');

      const isPlannedRowVisible = plannedRow && !plannedRow.classList.contains('is-hidden');
      const plannedPriceText = plannedPriceEl ? plannedPriceEl.textContent.trim() : '';
      const onetimePriceText = onetimePriceEl ? onetimePriceEl.textContent.trim() : '';

      let unitPrice;
      if (state.purchase === 'planned') {
        // Planned mode: use planned row text if visible (tier_2_price products),
        // else use activePlannedPrice from data attributes (regular subscription products)
        if (isPlannedRowVisible && plannedPriceText) {
          unitPrice = plannedPriceText;
        } else {
          unitPrice = formatMoney(pricing.activePlannedPrice || pricing.onetime);
        }
      } else {
        // One-time mode: always show one-time price from DOM
        unitPrice = onetimePriceText || formatMoney(pricing.onetime);
      }

      const li = document.createElement('li');
      li.className = 'spd-cart-item';
      li.dataset.variantId = variantId;
      li.innerHTML = `
        <div class="spd-cart-item__img-wrap">
          ${imgSrc ? `<img src="${imgSrc}" alt="${title}" loading="lazy">` : ''}
        </div>
        <div class="spd-cart-item__info">
          <span class="spd-cart-item__title">${title}</span>
          <span class="spd-cart-item__price">${unitPrice} <span class="spd-cart-item__x">×</span> ${qty}</span>
        </div>
        <button type="button" class="spd-cart-item__remove" data-spd-remove-item data-variant-id="${variantId}" aria-label="Remove ${title}">×</button>
      `;
      listEl.appendChild(li);
    });

    if (emptyEl) emptyEl.classList.toggle('is-hidden', hasItems);
    listEl.classList.toggle('is-hidden', !hasItems);
  };


  const getUiQuantity = (product) => {
    const input = product.querySelector('.spd-qty__input');
    return Math.max(0, parseInt(input?.value || '0', 10));
  };

  const updateBubbleInstant = () => {
    const boxTotal = Array.from(cartState.items.values()).reduce((sum, qty) => sum + (qty || 0), 0);
    const totalGlobal = boxTotal + (cartState.initialOtherItemsCount || 0);

    document.querySelectorAll('#cart-icon-bubble').forEach((container) => {
      let bubble = container.querySelector('.cart-count-bubble');
      const span = bubble ? bubble.querySelector('span[aria-hidden=\"true\"]') : null;

      if (totalGlobal > 0) {
        if (!bubble) {
          container.insertAdjacentHTML(
            'beforeend',
            `<div class=\"cart-count-bubble\">
               <span aria-hidden=\"true\">${totalGlobal}</span>
               <span class=\"visually-hidden\">${totalGlobal} items</span>
             </div>`
          );
        } else if (span) {
          span.textContent = totalGlobal;
          bubble.style.display = 'block';
        }
      } else if (bubble) {
        bubble.style.display = 'none';
      }
    });
  };

  const getPriceForProduct = (product) => {
    return getProductPricingModel(product).activePrice;
  };

  const updateLocalTotal = () => {
    const totalEl = root.querySelector('[data-spd-total]');
    const compareEl = root.querySelector('[data-spd-total-compare]');
    const saveEl = root.querySelector('[data-spd-total-save]');
    if (!totalEl) return;
    let finalTotal = 0;
    let baseTotal = 0;
    // A product can appear under more than one category tab; count it once.
    const seen = new Set();

    root.querySelectorAll('.spd-product').forEach((product) => {
      const variantId = parseInt(product.dataset.variantId || '0', 10);
      if (seen.has(variantId)) return;
      seen.add(variantId);
      const qty = cartState.items.get(variantId) || 0;
      if (qty <= 0) return;
      const pricing = getProductPricingModel(product);
      const finalPrice = pricing.activePrice;
      baseTotal += pricing.onetime * qty;
      finalTotal += finalPrice * qty;
    });

    const savings = Math.max(0, baseTotal - finalTotal);
    totalEl.textContent = formatMoney(finalTotal);

    if (compareEl) {
      compareEl.textContent = formatMoney(baseTotal);
      compareEl.classList.toggle('is-hidden', state.purchase === 'one-time' || savings <= 0 || baseTotal <= 0);
    }

    if (saveEl) {
      if (state.purchase === 'one-time' || savings <= 0 || baseTotal <= 0) {
        saveEl.textContent = '';
        saveEl.classList.add('is-hidden');
      } else {
        saveEl.textContent = `You save ${formatMoney(savings)}`;
        saveEl.classList.remove('is-hidden');
      }
    }

    renderCartList();
  };

  const getFrequencyKey = () => {
    if (state.purchase === 'one-time') return 'one-time';
    return state.frequency || '4w';
  };

  // _dynamicBoxIndex is the box-level identifier, NOT a per-item slot number.
  // All items in the same SKIO Dynamic Box must share the same index (0)
  // so SKIO groups them into one unified, editable subscription box.
  // Using sequential indices per item (0, 1, 2...) causes SKIO to treat each
  // item as its own separate box — the "every item is its own box" symptom.
  //
  // _frequency is written into cart properties so inferCartSelection() can
  // read it directly instead of reverse-engineering it from selling_plan IDs.
  const buildCartProperties = (boxId, frequency) => ({
    _dynamicBoxIndex: 0,
    _dynamicBoxId: boxId || skioBoxId || '',
    'Build a Box': 'Item',
    _frequency: frequency || 'one-time',
  });

  const shouldHandleCartItem = (item, sectionIds) => {
    return sectionIds.has(Number(item.variant_id));
  };

  const getFrequencyFromPlan = (product, sellingPlanId) => {
    const planId = Number(sellingPlanId);
    if (!Number.isFinite(planId) || !product) return '';

    const ids2w = [
      Number(getPlanAttr(product, 'plan-id-2w-member') || 0),
      Number(getPlanAttr(product, 'plan-id-2w-std') || 0),
    ];
    const ids4w = [
      Number(getPlanAttr(product, 'plan-id-4w-member') || 0),
      Number(getPlanAttr(product, 'plan-id-4w-std') || 0),
    ];

    if (ids2w.includes(planId)) return '2w';
    if (ids4w.includes(planId)) return '4w';
    return '';
  };

  const inferCartSelection = (cartData) => {
    if (!cartData || !Array.isArray(cartData.items)) return null;
    const sectionIds = getSectionVariantIds();
    const relevant = cartData.items.filter((item) => shouldHandleCartItem(item, sectionIds) && Number(item.quantity) > 0);
    if (!relevant.length) return null;

    const score = { 'one-time': 0, '2w': 0, '4w': 0 };

    relevant.forEach((item) => {
      let frequency = '';
      const propFrequency = item.properties?._frequency;
      if (propFrequency === 'one-time' || propFrequency === '2w' || propFrequency === '4w') {
        frequency = propFrequency;
      }

      if (!frequency) {
        const variantId = Number(item.variant_id);
        const product = root.querySelector(`.spd-product[data-variant-id="${variantId}"]`);
        frequency = getFrequencyFromPlan(product, item.selling_plan_allocation?.selling_plan?.id);
      }

      if (!frequency) {
        frequency = item.selling_plan_allocation?.selling_plan ? '4w' : 'one-time';
      }

      score[frequency] += Number(item.quantity) || 1;
    });

    if (score['one-time'] >= score['2w'] && score['one-time'] >= score['4w']) {
      return { purchase: 'one-time', frequency: state.frequency || '4w' };
    }
    if (score['2w'] >= score['4w']) {
      return { purchase: 'planned', frequency: '2w' };
    }
    return { purchase: 'planned', frequency: '4w' };
  };

  const syncSelectionFromCart = (cartData) => {
    const inferred = inferCartSelection(cartData);
    if (!inferred) return;

    if (inferred.purchase !== state.purchase) {
      setPurchaseType(inferred.purchase, { syncFrequency: false, triggerCartUpdate: false });
    }

    if (inferred.purchase === 'planned' && inferred.frequency !== state.frequency) {
      setFrequency(inferred.frequency, { syncPurchase: false, triggerCartUpdate: false });
    }
  };

  const updateCartItemsForFrequency = async () => {
    const sectionIds = getSectionVariantIds();
    if (!sectionIds.size) return;

    toggleGlobalLoading(true);

    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const cart = await res.json();

      const changes = [];
      const sectionKeys = [
        'cart-icon-bubble',
        'cart-drawer',
        'cart-live-region-text',
      ];



      for (let index = 0; index < cart.items.length; index += 1) {
        const item = cart.items[index];
        if (!shouldHandleCartItem(item, sectionIds)) continue;

        const variantId = Number(item.variant_id);
        const product = root.querySelector(`.spd-product[data-variant-id="${variantId}"]`);
        if (!product) continue;

        let desiredPlanId = state.purchase === 'planned'
          ? getPlanIdFromDataset(product, state.frequency)
          : '';
        if (!desiredPlanId && state.purchase === 'planned') {
          const meta = await fetchProductMeta(product.dataset.productHandle);
          desiredPlanId = getPlanIdFromMeta(meta, state.frequency) || '';
        }
        const currentPlanId = item.selling_plan_allocation?.selling_plan?.id
          ? Number(item.selling_plan_allocation.selling_plan.id)
          : 0;

        const itemBoxId = skioBoxId || item.properties?._dynamicBoxId || root.dataset.boxId || '';
        // All items share _dynamicBoxIndex 0 — they all belong to one box
        const frequencyValue = state.purchase === 'planned' ? state.frequency : 'one-time';
        const properties = {
          ...(item.properties || {}),
          ...buildCartProperties(itemBoxId, frequencyValue),
        };

        const hasMatchingPlan = desiredPlanId
          ? Number(desiredPlanId) === currentPlanId
          : currentPlanId === 0;
        const hasMatchingProperties =
          item.properties?.['Build a Box'] === 'Item'
          && item.properties?._dynamicBoxId === itemBoxId
          && Number(item.properties?._dynamicBoxIndex) === 0
          && item.properties?._frequency === frequencyValue;

        if (hasMatchingPlan && hasMatchingProperties) {
          continue;
        }

        changes.push({
          line: index + 1,
          quantity: item.quantity,
          selling_plan: desiredPlanId ? Number(desiredPlanId) : null,
          properties,
        });
      }

      if (!changes.length) return;

      let finalCart = null;
      for (let i = changes.length - 1; i >= 0; i -= 1) {
        const payload = { ...changes[i] };
        if (i === 0) {
          payload.sections = sectionKeys;
          payload.sections_url = window.location.pathname;
        }
        const changeRes = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'same-origin',
        });
        if (!changeRes.ok) {
          throw new Error('Cart change failed');
        }
        if (i === 0) {
          finalCart = await changeRes.json();
        } else {
          await changeRes.json().catch(() => { });
        }
      }

      if (finalCart) {
        if (finalCart.sections) {
          updateCartSectionsFromData(finalCart.sections);
        }
        syncCartState(finalCart);
        triggerShopifyCartUpdate(finalCart);
        await updateCartSummary(finalCart);
      } else {
        requestCartSync();
      }
    } catch (err) {
      console.error(err);
      requestCartSync();
    } finally {
      toggleGlobalLoading(false);
    }
  };

  const scheduleFrequencyCartUpdate = () => {
    clearTimeout(frequencyUpdateTimer);
    frequencyUpdateTimer = setTimeout(() => {
      updateCartItemsForFrequency();
    }, 300);
  };

  // FIX #2: Replace the blunt /cart/update.js (variant-ID-keyed) approach
  // with a targeted /cart/change.js call using the line item's unique `key`.
  //
  // The original code used:
  //   POST /cart/update.js  { updates: { [variantId]: qty } }
  //
  // That endpoint matches by variant ID and can corrupt SKIO box items when
  // there are multiple line items sharing the same variant (which SKIO
  // sometimes creates internally). Using the line `key` from /cart.js
  // targets exactly the right line item regardless of duplicates.
  const commitQtyUpdate = async (variantId) => {
    const qty = pendingQtyUpdates.get(variantId);
    pendingQtyUpdates.delete(variantId);
    if (!Number.isFinite(qty)) return;

    try {
      toggleGlobalLoading(true);

      // Fetch the current cart to get the line item key for this variant.
      const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!cartRes.ok) throw new Error('Cart fetch failed');
      const cart = await cartRes.json();

      // Find the SPD-managed line item for this variant (identified by
      // _dynamicBoxId so we target the box item, not a stray one-off item).
      const lineItem = cart.items.find(
        (item) =>
          Number(item.variant_id) === Number(variantId) &&
          item.properties?.['Build a Box'] === 'Item'
      ) || cart.items.find(
        (item) => Number(item.variant_id) === Number(variantId)
      );

      if (!lineItem) {
        // Item is no longer in cart — nothing to update.
        return;
      }

      const changeRes = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: lineItem.key,   // line item key — unique per line, not per variant
          quantity: qty,
        }),
        credentials: 'same-origin',
      });

      if (!changeRes.ok) throw new Error('Cart change failed');
      const cartData = await changeRes.json();
      syncCartState(cartData);
      triggerShopifyCartUpdate(cartData);
      await updateCartSummary(cartData);
    } catch (err) {
      console.error(err);
      requestCartSync();
    } finally {
      toggleGlobalLoading(false);
    }
  };

  const scheduleQtyUpdate = (variantId, qty) => {
    if (!variantId) return;
    pendingQtyUpdates.set(variantId, qty);
    clearTimeout(qtyUpdateTimers.get(variantId));
    qtyUpdateTimers.set(
      variantId,
      setTimeout(() => {
        commitQtyUpdate(variantId);
      }, 350)
    );
  };

  const maybeSyncCartQty = (product) => {
    if (!product) return;
    const variantId = parseInt(product.dataset.variantId || '0', 10);
    if (!variantId) return;
    if (!cartState.items.has(variantId)) return;
    const qty = getUiQuantity(product);
    scheduleQtyUpdate(variantId, qty);
  };

  const syncCartState = (cartData) => {
    if (!cartData || isDuplicateCartData(cartData)) return;
    syncSelectionFromCart(cartData);
    const sectionIds = getSectionVariantIds();
    const map = new Map();
    let sectionCount = 0;

    cartData.items.forEach((item) => {
      const variantId = Number(item.variant_id);
      const qty = Number(item.quantity) || 0;
      if (!sectionIds.has(variantId)) return;
      map.set(variantId, (map.get(variantId) || 0) + qty);
      sectionCount += qty;
    });
    pendingAdds.forEach((qty, variantId) => {
      map.set(variantId, (map.get(variantId) || 0) + qty);
    });

    cartState.items = map;
    cartState.initialOtherItemsCount = Math.max(0, (cartData.item_count || 0) - sectionCount);
    syncProductUIs();
    updateBubbleInstant();
    updateLocalTotal();
    setLastCartStamp(cartData);
  };

  const updateCartCountBubble = (cartData) => {
    if (!cartData) return;
    const count = cartData.item_count || 0;
    const containers = document.querySelectorAll('.cart-count-bubble');
    if (containers.length === 0 && count > 0) {
      const icon = document.getElementById('cart-icon-bubble');
      if (icon) {
        icon.innerHTML = `<div class="cart-count-bubble"><span aria-hidden="true">${count}</span><span class="visually-hidden">${count} items</span></div>`;
      }
      return;
    }
    containers.forEach((bubble) => {
      const span = bubble.querySelector('span[aria-hidden="true"]');
      if (span) span.textContent = count;
      bubble.style.display = count > 0 ? 'block' : 'none';
    });
  };

  const updateCartSectionsFromData = (sections) => {
    if (!sections) return;

    if (sections['cart-drawer']) {
      const cartDrawerElement = document.querySelector('cart-drawer');
      const cartDrawerContent = document.querySelector('#CartDrawer');
      if (cartDrawerContent) {
        const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const newDrawerContent = doc.querySelector('#CartDrawer');
        if (newDrawerContent) {
          cartDrawerContent.innerHTML = newDrawerContent.innerHTML;
          if (cartDrawerElement) {
            if (newDrawerContent.querySelector('.cart-drawer__warnings')) {
              cartDrawerElement.classList.add('is-empty');
            } else {
              cartDrawerElement.classList.remove('is-empty');
            }

            const overlay = cartDrawerElement.querySelector('#CartDrawer-Overlay');
            if (overlay) {
              overlay.onclick = () => cartDrawerElement.close();
            }
          }
        }
      }
    }

    if (sections['cart-icon-bubble']) {
      const icon = document.getElementById('cart-icon-bubble');
      if (icon) {
        icon.innerHTML = sections['cart-icon-bubble'];
        const count = icon.querySelector('.cart-count-bubble span[aria-hidden="true"]');
        if (count && parseInt(count.innerText, 10) > 0) {
          const bubble = icon.querySelector('.cart-count-bubble');
          if (bubble) bubble.style.display = 'block';
        }
      }
    }

  };

  const refreshCartSections = async () => {
    const sections = ['cart-icon-bubble', 'cart-drawer'];
    try {
      const res = await fetch(`/?sections=${sections.join(',')}`, { credentials: 'same-origin' });
      if (!res.ok) return;
      updateCartSectionsFromData(await res.json());
    } catch (err) {
      console.error(err);
    }
  };

  const triggerShopifyCartUpdate = (cartData = null) => {
    if (window.PUB_SUB_EVENTS && window.publish) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'subscription-planned-delivery',
        cartData,
      });
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cartData } }));
    document.dispatchEvent(new CustomEvent('cart:build', { detail: { cart: cartData } }));

    if (cartData && cartData.sections) {
      updateCartSectionsFromData(cartData.sections);
    } else {
      refreshCartSections();
    }
    updateCartCountBubble(cartData);
  };

  const updateCartSummary = async (cartData = null) => {
    const totalEl = root.querySelector('[data-spd-total]');
    const threshold = parseFloat(root.dataset.freeShipping || '0');
    const minOrder = parseFloat(root.dataset.minOrder || '0');
    const shippingGoal = parseFloat(root.dataset.shippingGoal || threshold || '0');

    const updateCheckoutState = (totalCents = 0, itemCount = 0) => {
      const emptyMain = root.dataset.msgEmptyMain || 'Add Products To Start Your Order';
      const minMain = root.dataset.msgMinMain || 'Order Minimum Not Met';
      const minSubTemplate = root.dataset.msgMinSub || 'Add [amount] to checkout';
      const goalMain = root.dataset.msgGoalMain || 'Checkout Now';
      const goalSubTemplate = root.dataset.msgGoalSub || 'Add [amount] for Free Shipping to Select Areas';
      const freeMain = root.dataset.msgFreeMain || 'Checkout Now';
      const freeSub = root.dataset.msgFreeSub || 'Free Shipping Unlocked!';

      if (!checkoutBtn || !checkoutMain || !checkoutSub) return;

      if (itemCount === 0) {
        checkoutMain.textContent = emptyMain;
        checkoutSub.textContent = '';
        checkoutBtn.classList.remove('is-ready');
        checkoutBtn.setAttribute('aria-disabled', 'true');
        return;
      }

      const totalDollars = totalCents / 100;

      if (minOrder > 0 && totalDollars < minOrder) {
        const remaining = Math.max(0, minOrder - totalDollars);
        checkoutMain.textContent = minMain;
        checkoutSub.textContent = minSubTemplate.replace('[amount]', formatMoney(Math.round(remaining * 100)));
        checkoutBtn.classList.remove('is-ready');
        checkoutBtn.setAttribute('aria-disabled', 'true');
        checkoutBtn.setAttribute('disabled', 'true');
        checkoutBtn.classList.add('is-disabled');
        return;
      }

      if (shippingGoal > 0 && totalDollars < shippingGoal) {
        const remaining = Math.max(0, shippingGoal - totalDollars);
        checkoutMain.textContent = goalMain;
        checkoutSub.textContent = goalSubTemplate.replace('[amount]', formatMoney(Math.round(remaining * 100)));
        checkoutBtn.classList.add('is-ready');
        checkoutBtn.removeAttribute('aria-disabled');
        checkoutBtn.removeAttribute('disabled');
        checkoutBtn.classList.remove('is-disabled');
        return;
      }

      checkoutMain.textContent = freeMain;
      checkoutSub.textContent = freeSub;
    };

    if (cartData) {
      if (totalEl) totalEl.textContent = formatMoney(cartData.total_price || 0);
      updateCheckoutState(cartData.total_price || 0, cartData.item_count || 0);
      return;
    }

    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const cart = await res.json();
      if (totalEl) totalEl.textContent = formatMoney(cart.total_price || 0);
      updateCheckoutState(cart.total_price || 0, cart.item_count || 0);
    } catch (err) {
      console.error(err);
    }
  };

  const debounce = (fn, wait = 200) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const updateNextDeliveryDate = () => {
    const dateEl = root.querySelector('[data-spd-footer-date]');
    if (!dateEl) return;

    const today = new Date();
    let nextDeliveryDate;

    if (state.frequency === '2w') {
      nextDeliveryDate = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);
    } else {
      nextDeliveryDate = new Date(today.getTime() + 28 * 24 * 60 * 60 * 1000);
    }

    const options = { month: 'short', day: 'numeric' };
    const formattedDate = nextDeliveryDate.toLocaleDateString('en-US', options);
    dateEl.textContent = `Next Delivery: approx ${formattedDate}`;
  };

  const requestCartSync = async (cartData = null) => {
    if (cartData && cartData.items) {
      syncCartState(cartData);
      await updateCartSummary(cartData);
      return;
    }

    try {
      if (syncInFlight) {
        syncQueued = true;
        return;
      }
      syncInFlight = true;
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const latestCart = await res.json();
      syncCartState(latestCart);
      await updateCartSummary(latestCart);
    } catch (err) {
      console.error(err);
    } finally {
      syncInFlight = false;
      if (syncQueued) {
        syncQueued = false;
        requestCartSync();
      }
    }
  };

  let pendingCartData = null;
  const scheduleCartSync = debounce(() => {
    const data = pendingCartData;
    pendingCartData = null;
    requestCartSync(data);
  }, 150);

  const queueCartSync = (cartData = null) => {
    if (cartData && isDuplicateCartData(cartData)) {
      return;
    }
    if (cartData) {
      pendingCartData = cartData;
    }
    scheduleCartSync();
  };

  // Adds run one at a time (so responses can't overwrite each other), but every tap
  // gets instant feedback: the button spins and the footer shows the item right away,
  // instead of the whole section dimming until the network round trips finish.
  let addQueue = Promise.resolve();

  const submitAdd = async ({ button, product, variantId, quantity, item, isSubscriptionFrequency, frequency }) => {
    try {
      if (isSubscriptionFrequency && !item.selling_plan) {
        // Only reached when the product markup has no plan IDs for this frequency.
        const meta = await fetchProductMeta(product.dataset.productHandle);
        const planIdValue = parseInt(getPlanIdFromMeta(meta, frequency) || '', 10);
        if (Number.isFinite(planIdValue)) item.selling_plan = planIdValue;
      }

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Ask for the drawer/bubble HTML in the same response so we don't need a
        // separate /?sections= round trip afterwards.
        body: JSON.stringify({
          items: [item],
          sections: ['cart-icon-bubble', 'cart-drawer'],
          sections_url: window.location.pathname,
        }),
        credentials: 'same-origin',
      });

      if (!res.ok) throw new Error('Add to cart failed');
      const added = await res.json();

      const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
      const cartData = cartRes.ok ? await cartRes.json() : null;

      releasePendingAdd(variantId, quantity);
      if (cartData) {
        if (added && added.sections) cartData.sections = added.sections;
        syncCartState(cartData);
        triggerShopifyCartUpdate(cartData);
        await updateCartSummary(cartData);
      } else {
        if (added && added.sections) updateCartSectionsFromData(added.sections);
        requestCartSync();
      }
    } catch (err) {
      console.error(err);
      releasePendingAdd(variantId, quantity);
      requestCartSync();
      button.textContent = 'Try Again';
      setTimeout(() => {
        updateProductUI(product, cartState.items.get(variantId) || 0);
      }, 1200);
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  };

  const addToCart = (button) => {
    const product = button.closest('.spd-product');
    if (!product || button.classList.contains('is-loading')) return;

    const inventory = getInventoryState(product);
    if (inventory.isOutOfStock) {
      showToast(getMaxAvailableMessage(0));
      updateProductUI(product, 0);
      return;
    }

    const input = product.querySelector('.spd-qty__input');
    let quantity = parseInteger(input?.value || '0', 0);
    if (!quantity || quantity < 0) quantity = 1;
    quantity = setProductQuantityValue(product, quantity, { showLimitToast: true });
    if (!quantity) {
      return;
    }

    const variantId = parseInt(product.dataset.variantId || '0', 10);
    if (!variantId) return;

    const isSubscriptionFrequency = state.purchase === 'planned' && (state.frequency === '2w' || state.frequency === '4w');
    const planId = isSubscriptionFrequency ? getPlanIdFromDataset(product, state.frequency) : '';

    const boxId = product.dataset.boxId || root.dataset.boxId || '';

    const frequencyValue = isSubscriptionFrequency ? state.frequency : 'one-time';
    const item = {
      id: variantId,
      quantity,
      properties: buildCartProperties(boxId, frequencyValue),
    };
    const planIdValue = parseInt(planId || '', 10);
    if (isSubscriptionFrequency && Number.isFinite(planIdValue)) {
      item.selling_plan = planIdValue;
    }

    // Immediate feedback, before any network work.
    button.disabled = true;
    button.classList.add('is-loading');
    pendingAdds.set(variantId, (pendingAdds.get(variantId) || 0) + quantity);
    cartState.items.set(variantId, (cartState.items.get(variantId) || 0) + quantity);
    updateBubbleInstant();
    updateLocalTotal();

    const job = { button, product, variantId, quantity, item, isSubscriptionFrequency, frequency: state.frequency };
    addQueue = addQueue.then(() => submitAdd(job));
    return addQueue;
  };

  const setActiveTab = (tabId) => {
    root.querySelectorAll('.spd-cat-button').forEach((button) => {
      button.classList.toggle('is-active', button.dataset.tab === tabId);
    });
    root.querySelectorAll('.spd-product-list').forEach((list) => {
      list.classList.toggle('is-active', list.dataset.tabTarget === tabId);
    });
  };

  purchaseButtons.forEach((button) => {
    button.addEventListener('click', () => setPurchaseType(button.dataset.purchase));
  });

  frequencyInputs.forEach((input) => {
    input.addEventListener('change', () => {
      if (input.checked) {
        setFrequency(input.value);
        updateNextDeliveryDate();
      }
    });
  });

  root.addEventListener('click', (event) => {
    const tolstoyArrowBtn = event.target.closest('.tolstoy-stories-next-button, .tolstoy-stories-previous-button');
    if (tolstoyArrowBtn) {
      event.preventDefault();
      event.stopPropagation();

      const wrapper = tolstoyArrowBtn.closest('tolstoy-stories') || tolstoyArrowBtn.closest('._storiesVideoContainer_1v61q_12');
      const container =
        wrapper?.querySelector('.tolstoy-stories-tiles-container') ||
        wrapper?.querySelector('._tilesContainer_1v61q_19');

      if (container) {
        const scrollAmount = 160;
        const isNext =
          tolstoyArrowBtn.classList.contains('tolstoy-stories-next-button') ||
          tolstoyArrowBtn.classList.contains('_nextArrowButtonContainer_1v61q_211');
        container.scrollBy({ left: isNext ? scrollAmount : -scrollAmount, behavior: 'smooth' });
        setTimeout(() => updateTolstoyArrowVisibility(container), 350);
      }
      return;
    }

    const removeBtn = event.target.closest('[data-spd-remove-item]');
    if (removeBtn) {
      if (root.classList.contains('is-loading')) return;
      const variantId = parseInt(removeBtn.dataset.variantId || '0', 10);
      if (!variantId) return;
      const product = root.querySelector(`.spd-product[data-variant-id="${variantId}"]`);
      if (product) {
        const input = product.querySelector('.spd-qty__input');
        if (input) input.value = 0;
      }
      cartState.items.set(variantId, 0);
      scheduleQtyUpdate(variantId, 0);
      syncProductUIs();
      updateLocalTotal();
      return;
    }

    const tabButton = event.target.closest('.spd-cat-button');
    if (tabButton) {
      setActiveTab(tabButton.dataset.tab);
      return;
    }

    const minus = event.target.closest('[data-qty="minus"]');
    const plus = event.target.closest('[data-qty="plus"]');

    if (minus || plus) {
      const product = event.target.closest('.spd-product');
      const input = product?.querySelector('.spd-qty__input');
      if (!input) return;
      const current = parseInteger(input.value || '0', 0);
      if (plus) {
        setProductQuantityValue(product, current + 1, { showLimitToast: true });
      }
      if (minus) {
        setProductQuantityValue(product, Math.max(0, current - 1));
      }
      maybeSyncCartQty(product);
      return;
    }

    const addBtn = event.target.closest('.spd-add-btn');
    if (addBtn) {
      addToCart(addBtn);
      return;
    }

    const learnBtn = event.target.closest('[data-spd-learn-more]');
    if (learnBtn) {
      const product = learnBtn.closest('.spd-product');
      const panel = product?.querySelector('[data-spd-learn-panel]');
      if (!product || !panel) return;
      const isOpen = !panel.hasAttribute('hidden');
      if (isOpen) {
        panel.setAttribute('hidden', '');
        learnBtn.setAttribute('aria-expanded', 'false');
        learnBtn.textContent = 'Learn More';
        return;
      }

      const wasLoaded = panel.dataset.learnLoaded === 'true';
      if (!wasLoaded) learnBtn.textContent = 'Loading…';
      loadLearnPanel(panel).then(() => {
        panel.removeAttribute('hidden');
        learnBtn.setAttribute('aria-expanded', 'true');
        learnBtn.textContent = 'Hide Details';
        refreshTolstoyInPanel(panel);
      });
      return;
    }

    const metaToggle = event.target.closest('[data-spd-meta-toggle]');
    if (metaToggle) {
      const accordion = metaToggle.closest('.spd-meta-accordion');
      const content = accordion?.querySelector('[data-spd-meta-content]');
      if (!accordion || !content) return;

      const isOpen = !content.hasAttribute('hidden');
      const container = accordion.parentElement;
      container?.querySelectorAll('.spd-meta-accordion').forEach((item) => {
        const block = item.querySelector('[data-spd-meta-content]');
        const header = item.querySelector('[data-spd-meta-toggle]');
        if (block) block.setAttribute('hidden', '');
        if (header) header.setAttribute('aria-expanded', 'false');
      });

      if (isOpen) {
        content.setAttribute('hidden', '');
        metaToggle.setAttribute('aria-expanded', 'false');
      } else {
        content.removeAttribute('hidden');
        metaToggle.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    const thumb = event.target.closest('[data-spd-thumb]');
    if (thumb) {
      const gallery = thumb.closest('.spd-learn-gallery');
      const mainImage = gallery?.querySelector('[data-spd-main-image]');
      const nextImageUrl = thumb.dataset.imageUrl;
      if (!gallery || !mainImage || !nextImageUrl) return;

      mainImage.src = nextImageUrl;
      gallery.querySelectorAll('[data-spd-thumb]').forEach((item) => item.classList.remove('is-active'));
      thumb.classList.add('is-active');
    }
  });

  root.addEventListener('input', (event) => {
    const qtyInput = event.target.closest('.spd-qty__input');
    if (!qtyInput) return;
    const product = qtyInput.closest('.spd-product');
    setProductQuantityValue(product, qtyInput.value, { showLimitToast: true });
    maybeSyncCartQty(product);
  });

  document.addEventListener('cart:updated', (event) => {
    queueCartSync(event?.detail?.cart || null);
  });

  document.addEventListener('cart:change', (event) => {
    queueCartSync(event?.detail?.cart || null);
  });

  document.addEventListener('cart:build', (event) => {
    queueCartSync(event?.detail?.cart || null);
  });

  document.addEventListener('spd:cart-updated', (event) => {
    queueCartSync(event?.detail?.cart || null);
  });

  document.addEventListener('spd:cart-drawer-updated', (event) => {
    queueCartSync(event?.detail?.cart || null);
  });

  document.addEventListener('spd:cart-rendered', () => {
    queueCartSync();
  });

  window.addEventListener('resize', () => {
    root.querySelectorAll('[data-spd-learn-panel]:not([hidden])').forEach((panel) => {
      attachTolstoyListeners(panel);
    });
  });

  const subscribeFn = window.subscribe || null;
  if (subscribeFn && window.PUB_SUB_EVENTS?.cartUpdate) {
    subscribeFn(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
      queueCartSync(event?.cartData || null);
    });
  }

  updateHeader();
  updateCartSummary();
  updateLocalTotal();
  updateNextDeliveryDate();
  root.querySelectorAll('.spd-product').forEach((product) => {
    syncProductAvailabilityUI(product);
  });

  (async () => {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const cartData = await res.json();
      syncCartState(cartData);
      await updateCartSummary(cartData);
    } catch (err) {
      console.error(err);
    }
  })();

  // Initialize view state
  setPurchaseType(state.purchase, { triggerCartUpdate: false });
})();
